'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Triangle } from 'lucide-react';

interface GanttTask {
    id: string;
    title: string;
    date: string;
    endDate?: string;
    status: string;
    projectId: string;
    projectName: string;
    projectColor: string;
}

interface Milestone {
    date: string;
    label: string;
}

const STATUS_COLORS: Record<string, string> = {
    'done': '#22c55e',
    'in-progress': '#3b82f6',
    'todo': '#6b7280',
    'issue': '#ef4444',
};

function getDaysBetween(start: string, end: string): number {
    return Math.ceil((new Date(end).getTime() - new Date(start).getTime()) / 86400000);
}

function addDays(date: string, days: number): string {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

export default function GanttChart({ isDark = true }: { isDark?: boolean }) {
    const [tasks, setTasks] = useState<GanttTask[]>([]);
    const [milestones, setMilestones] = useState<Milestone[]>([]);
    const [loading, setLoading] = useState(true);
    const [dragTask, setDragTask] = useState<{ id: string; startX: number; startDate: string } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [zoom, setZoom] = useState(28); // px per day

    // Load data
    useEffect(() => {
        fetch('/api/schedule')
            .then(r => r.json())
            .then(data => {
                const projects = data.projects || [];
                const rawTasks = data.tasks || [];
                const mapped: GanttTask[] = rawTasks.map((t: any) => {
                    const proj = projects.find((p: any) => p.id === t.projectId) || { name: '', color: '#6b7280' };
                    return {
                        id: t.id,
                        title: t.title?.replace(/^[■*\s]+/, ''),
                        date: t.date,
                        endDate: t.date, // single-day tasks
                        status: t.status,
                        projectId: t.projectId,
                        projectName: proj.name || '',
                        projectColor: proj.color || '#5dade2',
                    };
                });
                setTasks(mapped);

                // Auto-detect milestones from task names
                const ms: Milestone[] = [];
                mapped.forEach(t => {
                    if (t.title.includes('마감') || t.title.includes('제출') || t.title.includes('납품') || t.title.includes('완료')) {
                        ms.push({ date: t.date, label: t.title.slice(0, 15) });
                    }
                });
                setMilestones(ms);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    // Calculate range
    const allDates = tasks.map(t => t.date).filter(Boolean);
    const minDate = allDates.length > 0 ? allDates.sort()[0] : new Date().toISOString().slice(0, 10);
    const maxDate = allDates.length > 0 ? allDates.sort().reverse()[0] : addDays(minDate, 42);
    const chartStart = addDays(minDate, -3);
    const chartEnd = addDays(maxDate, 7);
    const totalDays = getDaysBetween(chartStart, chartEnd);

    // Group by project
    const projectGroups = new Map<string, GanttTask[]>();
    tasks.forEach(t => {
        const key = t.projectName || '기타';
        if (!projectGroups.has(key)) projectGroups.set(key, []);
        projectGroups.get(key)!.push(t);
    });

    const sortedProjectGroups = Array.from(projectGroups.entries()).sort((a, b) => {
        const maxA = a[1].map(t => t.date).sort().reverse()[0] || '';
        const maxB = b[1].map(t => t.date).sort().reverse()[0] || '';
        if (maxA !== maxB) return maxB.localeCompare(maxA);
        return a[0].localeCompare(b[0]);
    });

    // Generate day columns
    const days: { date: string; label: string; dayOfWeek: number }[] = [];
    for (let i = 0; i < totalDays; i++) {
        const d = addDays(chartStart, i);
        const dt = new Date(d);
        days.push({
            date: d,
            label: `${dt.getMonth() + 1}/${dt.getDate()}`,
            dayOfWeek: dt.getDay(),
        });
    }

    const today = new Date().toISOString().slice(0, 10);

    // Drag & drop handlers
    const handleDragStart = useCallback((taskId: string, e: React.MouseEvent) => {
        const task = tasks.find(t => t.id === taskId);
        if (!task) return;
        setDragTask({ id: taskId, startX: e.clientX, startDate: task.date });
    }, [tasks]);

    const handleDragMove = useCallback((e: React.MouseEvent) => {
        if (!dragTask) return;
        const dx = e.clientX - dragTask.startX;
        const dayDelta = Math.round(dx / zoom);
        if (dayDelta === 0) return;
        const newDate = addDays(dragTask.startDate, dayDelta);
        setTasks(prev => prev.map(t => t.id === dragTask.id ? { ...t, date: newDate, endDate: newDate } : t));
    }, [dragTask, zoom]);

    const handleDragEnd = useCallback(async () => {
        if (!dragTask) return;
        const task = tasks.find(t => t.id === dragTask.id);
        if (task && task.date !== dragTask.startDate) {
            // Optimistic Update는 이미 handleDragMove에서 수행됨
            try {
                const res = await fetch('/api/schedule', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        taskId: task.id,
                        date: task.date
                    }),
                });
                if (!res.ok) throw new Error('DB Sync Failed');
            } catch (err) {
                console.error('[GanttDrag] DB 저장 실패 — 롤백', err);
                // 롤백 (이전 날짜로 원복)
                setTasks(prev => prev.map(t => t.id === dragTask.id ? { ...t, date: dragTask.startDate, endDate: dragTask.startDate } : t));
            }
        }
        setDragTask(null);
    }, [dragTask, tasks]);

    // Find critical path (simplified: longest sequence of consecutive tasks per project)
    const criticalDates = new Set<string>();
    projectGroups.forEach((pts) => {
        const sorted = [...pts].sort((a, b) => a.date.localeCompare(b.date));
        if (sorted.length >= 3) {
            sorted.slice(-3).forEach(t => criticalDates.add(t.date));
        }
    });

    if (loading) {
        return (
            <div className={`rounded-2xl p-10 text-center ${isDark ? 'bg-[#11131a] border border-white/10' : 'bg-white border border-gray-200'}`}>
                <div className="animate-spin text-3xl mb-3">⏳</div>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>간트 차트 로딩 중...</p>
            </div>
        );
    }

    if (tasks.length === 0) {
        return (
            <div className={`rounded-2xl p-10 text-center ${isDark ? 'bg-[#11131a] border border-white/10' : 'bg-white border border-gray-200'}`}>
                <BarChart className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>표시할 일정이 없습니다</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>스케줄러 탭에서 태스크를 추가하면 간트 차트에 자동 반영됩니다</p>
            </div>
        );
    }

    return (
        <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-[#11131a] border border-white/10' : 'bg-white border border-gray-200 shadow-xl'}`}>
            {/* Toolbar */}
            <div className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3">
                    <BarChart className={`w-5 h-5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                    <h2 className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>프로젝트 간트차트</h2>
                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tasks.length}개 일정</span>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setZoom(z => Math.max(12, z - 4))} className={`px-2 py-1 rounded text-xs font-mono ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>−</button>
                    <span className={`text-xs font-mono px-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{zoom}px</span>
                    <button onClick={() => setZoom(z => Math.min(60, z + 4))} className={`px-2 py-1 rounded text-xs font-mono ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>+</button>
                </div>
            </div>

            {/* Legend */}
            <div className={`px-4 py-2 flex items-center gap-4 text-[10px] border-b ${isDark ? 'border-white/5 text-gray-500' : 'border-gray-100 text-gray-400'}`}>
                {Object.entries(STATUS_COLORS).map(([status, color]) => (
                    <div key={status} className="flex items-center gap-1">
                        <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: color }} />
                        <span>{status === 'done' ? '완료' : status === 'in-progress' ? '진행중' : status === 'todo' ? '미시작' : '이슈'}</span>
                    </div>
                ))}
                <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 flex items-center justify-center">
                        <Triangle className="w-2.5 h-2.5 text-red-500 fill-red-500" />
                    </div>
                    <span>마일스톤</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-2 rounded-sm bg-red-500/30 border border-red-500/50" />
                    <span>크리티컬 패스</span>
                </div>
            </div>

            {/* Chart */}
            <div
                ref={containerRef}
                className="overflow-x-auto"
                onMouseMove={dragTask ? handleDragMove : undefined}
                onMouseUp={dragTask ? handleDragEnd : undefined}
                onMouseLeave={dragTask ? handleDragEnd : undefined}
            >
                <div style={{ width: totalDays * zoom + 200, minWidth: '100%' }}>
                    {/* Date header */}
                    <div className={`flex border-b sticky top-0 z-10 ${isDark ? 'border-white/10 bg-[#11131a]' : 'border-gray-200 bg-white'}`}>
                        <div className={`flex-shrink-0 px-3 py-2 font-semibold text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`} style={{ width: 200 }}>프로젝트 / 일정</div>
                        <div className="flex">
                            {days.map((d, i) => {
                                const isToday = d.date === today;
                                const isSun = d.dayOfWeek === 0;
                                const isSat = d.dayOfWeek === 6;
                                return (
                                    <div
                                        key={d.date}
                                        className={`text-center text-[9px] py-1 border-r ${isToday ? 'bg-indigo-500/20 font-bold text-indigo-400' :
                                                isSun ? 'text-red-400 bg-red-500/5' :
                                                    isSat ? 'text-blue-400 bg-blue-500/5' :
                                                        isDark ? 'text-gray-600 border-white/5' : 'text-gray-400 border-gray-100'
                                            }`}
                                        style={{ width: zoom }}
                                    >
                                        {i % Math.max(1, Math.floor(4 / (zoom / 28))) === 0 ? d.label : ''}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Today line */}
                    <div className="relative">
                        {days.findIndex(d => d.date === today) >= 0 && (
                            <div
                                className="absolute top-0 bottom-0 w-0.5 bg-indigo-500 z-20 opacity-60"
                                style={{ left: 200 + days.findIndex(d => d.date === today) * zoom + zoom / 2 }}
                            />
                        )}

                        {/* Milestone markers */}
                        {milestones.map((ms, mi) => {
                            const idx = days.findIndex(d => d.date === ms.date);
                            if (idx < 0) return null;
                            return (
                                <div
                                    key={mi}
                                    className="absolute top-0 z-30"
                                    style={{ left: 200 + idx * zoom + zoom / 2 - 6 }}
                                    title={ms.label}
                                >
                                    <div className="text-red-500 text-xs flex justify-center">
                                        <Triangle className="w-3 h-3 text-red-500 fill-red-500" />
                                    </div>
                                </div>
                            );
                        })}

                        {/* Project rows */}
                        {sortedProjectGroups.map(([projName, projTasks]) => (
                            <div key={projName}>
                                {/* Project header */}
                                <div className={`flex border-b ${isDark ? 'border-white/5 bg-white/[0.02]' : 'border-gray-100 bg-gray-50/50'}`}>
                                    <div className="flex-shrink-0 px-3 py-1.5 flex items-center gap-2" style={{ width: 200 }}>
                                        <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: projTasks[0]?.projectColor || '#5dade2' }} />
                                        <span className={`text-xs font-bold truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{projName || '기타'}</span>
                                        <span className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{projTasks.length}</span>
                                    </div>
                                    <div style={{ flex: 1 }} />
                                </div>

                                {/* Task bars */}
                                {projTasks.sort((a, b) => b.date.localeCompare(a.date)).map(task => {
                                    const dayIdx = getDaysBetween(chartStart, task.date);
                                    const duration = task.endDate ? getDaysBetween(task.date, task.endDate) + 1 : 1;
                                    const isCritical = criticalDates.has(task.date);
                                    const color = STATUS_COLORS[task.status] || '#6b7280';

                                    return (
                                        <div key={task.id} className={`flex border-b ${isDark ? 'border-white/[0.03] hover:bg-white/[0.02]' : 'border-gray-50 hover:bg-gray-50/50'}`}>
                                            <div className="flex-shrink-0 px-3 py-1 flex items-center" style={{ width: 200 }}>
                                                <span className={`text-[11px] truncate ${task.status === 'done' ? 'line-through opacity-40' : ''} ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                    {task.title}
                                                </span>
                                            </div>
                                            <div className="relative flex-1" style={{ height: 26 }}>
                                                <motion.div
                                                    className={`absolute top-1 rounded-md cursor-grab active:cursor-grabbing shadow-sm ${isCritical ? 'ring-1 ring-red-500/50' : ''}`}
                                                    style={{
                                                        left: dayIdx * zoom,
                                                        width: Math.max(duration * zoom - 2, zoom * 0.8),
                                                        height: 18,
                                                        backgroundColor: color,
                                                        opacity: task.status === 'done' ? 0.5 : 0.85,
                                                    }}
                                                    initial={{ scaleX: 0 }}
                                                    animate={{ scaleX: 1 }}
                                                    transition={{ duration: 0.3 }}
                                                    onMouseDown={(e) => handleDragStart(task.id, e as unknown as React.MouseEvent)}
                                                    title={`${task.title}\n${task.date} · ${task.status}`}
                                                >
                                                    {zoom >= 24 && (
                                                        <span className="text-[8px] text-white font-medium px-1 truncate block leading-[18px]">
                                                            {task.title.slice(0, Math.floor(zoom * duration / 6))}
                                                        </span>
                                                    )}
                                                </motion.div>

                                                {/* Dependency arrows (simplified: sequential within project) */}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
